import { useRef, useState } from 'react';

import { ApiError } from '@/api/client';
import {
  useComponents,
  useOpenIncident,
  usePostIncidentUpdate,
  useResolveIncident,
} from '@/api/queries';
import { useToast } from '@/app/toasts';
import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/fields';
import { useFocusFirstInvalid } from '@/components/focus';
import type { Incident, IncidentActionStage, Scope } from '@/domain/types';
import { ApproverField, ErrorSummary } from '@/features/proposals/decision';
import { explainError, useApproverName } from '@/features/proposals/decisionState';

/**
 * The dialogs behind the Incidents page's three actions (CHG-055). They act on the page's
 * incident directly: none of them changes a component's status, which stays a proposal's job.
 * The person's name is the audit trail; the API logs it.
 */

const STAGE_OPTIONS: { value: IncidentActionStage; label: string }[] = [
  { value: 'investigating', label: 'Investigating' },
  { value: 'identified', label: 'Identified' },
  { value: 'monitoring', label: 'Monitoring' },
];

const RETRYABLE = 'Statuspage could not take it just now. Nothing was written; try again.';
/** The list is read from the page's public JSON, which its CDN can serve about 30 s stale (TDD 5.3). */
const LAG = 'The page can take up to 30 s to show it here.';
const UNCERTAIN =
  'Statuspage did not confirm. It may have been written: refresh before trying again.';

interface Explained {
  fields: Record<string, string>;
  message: string | null;
}

/**
 * Turn an incident action's failure into field errors and a dialog-level message. A 409 means
 * nothing was written: a title that is already open lands on the title field, anything else
 * (the incident was closed meanwhile) is the message. The API's `actor` is the name field.
 */
function explainIncidentError(error: unknown, titleField: boolean): Explained {
  if (error instanceof ApiError) {
    const failed = error.incidentPublishFailed;
    if (failed)
      return { fields: {}, message: failed.outcome === 'uncertain' ? UNCERTAIN : RETRYABLE };
    const conflict = error.incidentConflict;
    if (conflict) {
      return titleField && /title/i.test(conflict.detail)
        ? { fields: { title: conflict.detail }, message: null }
        : { fields: {}, message: conflict.detail };
    }
  }
  const explained = explainError(error);
  const { actor, ...rest } = explained.fields;
  return { fields: actor ? { ...rest, approver: actor } : rest, message: explained.message };
}

function StageSelect({
  value,
  onChange,
  disabled,
  error,
}: {
  value: IncidentActionStage;
  onChange: (stage: IncidentActionStage) => void;
  disabled: boolean;
  error: string | undefined;
}) {
  return (
    <Field label="Stage" error={error}>
      {(props) => (
        <Select
          {...props}
          name="stage"
          value={value}
          onChange={(e) => onChange(e.target.value as IncidentActionStage)}
          disabled={disabled}
        >
          {STAGE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/** The scope's components, by display name, as a list to tick. Nothing to tick draws nothing. */
function ComponentChecklist({
  legend,
  options,
  selected,
  onChange,
  disabled,
  error,
}: {
  legend: string;
  options: { id: string; name: string }[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  disabled: boolean;
  error: string | undefined;
}) {
  if (options.length === 0) return null;
  return (
    <fieldset className="m-0 p-0 border-0 min-w-0">
      <legend className="text-body font-medium text-ink mb-2">{legend}</legend>
      <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {options.map((option) => (
          <Checkbox
            key={option.id}
            label={option.name}
            checked={selected.has(option.id)}
            disabled={disabled}
            onChange={(e) => {
              const next = new Set(selected);
              if (e.target.checked) next.add(option.id);
              else next.delete(option.id);
              onChange(next);
            }}
          />
        ))}
      </div>
      {error ? (
        <p className="mt-1.5 text-meta text-danger-ink font-medium" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

function useScopeComponents(scope: Scope) {
  const view = useComponents(scope);
  return (view.data?.components ?? []).map((c) => ({
    id: c.config.capability_id,
    name: c.config.display_name,
  }));
}

interface DialogProps {
  scope: Scope;
  open: boolean;
  onClose: () => void;
}

/** New incident: a title, a first message, a stage and the components it affects (possibly none). */
export function OpenIncidentDialog({ scope, open, onClose }: DialogProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [stage, setStage] = useState<IncidentActionStage>('investigating');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useOpenIncident(scope);
  const options = useScopeComponents(scope);
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!title.trim()) local.title = 'Give the incident a title.';
    if (!body.trim()) local.body = 'Write a message.';
    if (!approver.name.trim()) local.approver = 'Enter your name.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    try {
      const result = await mutation.mutateAsync({
        actor: approver.name.trim(),
        title: title.trim(),
        body: body.trim(),
        stage,
        components: options.filter((o) => selected.has(o.id)).map((o) => o.id),
      });
      toast.push({
        tone: 'success',
        title: `Incident opened: ${title.trim()}`,
        description: result.link ? `${LAG} ${result.link}` : LAG,
      });
      onClose();
    } catch (error) {
      const explained = explainIncidentError(error, true);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New incident"
      size="lg"
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            Open incident
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Title" required error={fieldErrors.title} hint="Cannot be renamed later.">
          {(props) => (
            <Input
              {...props}
              name="title"
              autoComplete="off"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. CSV imports failing"
              disabled={mutation.isPending}
            />
          )}
        </Field>
        <Field label="First message" required error={fieldErrors.body} hint="Customer-facing.">
          {(props) => (
            <Textarea
              {...props}
              name="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="e.g. CSV import jobs are not completing. We are investigating."
              disabled={mutation.isPending}
            />
          )}
        </Field>
        <StageSelect
          value={stage}
          onChange={setStage}
          disabled={mutation.isPending}
          error={fieldErrors.stage}
        />
        <ComponentChecklist
          legend="Affected components (optional)"
          options={options}
          selected={selected}
          onChange={setSelected}
          disabled={mutation.isPending}
          error={fieldErrors.components}
        />
        <ApproverField
          label="Your name"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}

/** Post update: a message, optionally a new stage (backward too) and more affected components. */
export function IncidentUpdateDialog({
  scope,
  incident,
  open,
  onClose,
}: DialogProps & { incident: Incident }) {
  const current: IncidentActionStage = STAGE_OPTIONS.some((o) => o.value === incident.status)
    ? (incident.status as IncidentActionStage)
    : 'investigating';
  const [body, setBody] = useState('');
  const [stage, setStage] = useState<IncidentActionStage>(current);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = usePostIncidentUpdate(scope);
  const listed = new Set(incident.components.map((c) => c.id));
  const options = useScopeComponents(scope).filter((o) => !listed.has(o.id));
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!body.trim()) local.body = 'Write a message.';
    if (!approver.name.trim()) local.approver = 'Enter your name.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    const added = options.filter((o) => selected.has(o.id)).map((o) => o.id);
    try {
      await mutation.mutateAsync({
        id: incident.id,
        body: {
          actor: approver.name.trim(),
          body: body.trim(),
          ...(stage !== current ? { stage } : {}),
          ...(added.length > 0 ? { components: added } : {}),
        },
      });
      toast.push({ tone: 'success', title: `Update posted: ${incident.name}`, description: LAG });
      onClose();
    } catch (error) {
      const explained = explainIncidentError(error, false);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Post update: ${incident.name}`}
      size="lg"
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            Post update
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Message" required error={fieldErrors.body} hint="Customer-facing.">
          {(props) => (
            <Textarea
              {...props}
              name="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={mutation.isPending}
            />
          )}
        </Field>
        <StageSelect
          value={stage}
          onChange={setStage}
          disabled={mutation.isPending}
          error={fieldErrors.stage}
        />
        <ComponentChecklist
          legend="Add components (optional)"
          options={options}
          selected={selected}
          onChange={setSelected}
          disabled={mutation.isPending}
          error={fieldErrors.components}
        />
        <ApproverField
          label="Your name"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}

/** Close incident: a resolution message, then the incident leaves the page's open list. */
export function ResolveIncidentDialog({
  scope,
  incident,
  open,
  onClose,
}: DialogProps & { incident: Incident }) {
  const [body, setBody] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useResolveIncident(scope);
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!body.trim()) local.body = 'Write a message.';
    if (!approver.name.trim()) local.approver = 'Enter your name.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    try {
      await mutation.mutateAsync({
        id: incident.id,
        body: { actor: approver.name.trim(), body: body.trim() },
      });
      toast.push({ tone: 'success', title: `Incident closed: ${incident.name}`, description: LAG });
      onClose();
    } catch (error) {
      const explained = explainIncidentError(error, false);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Close incident: ${incident.name}`}
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} loading={mutation.isPending}>
            Close incident
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Resolution message" required error={fieldErrors.body} hint="Customer-facing.">
          {(props) => (
            <Textarea
              {...props}
              name="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="e.g. The issue is resolved."
              disabled={mutation.isPending}
            />
          )}
        </Field>
        <ApproverField
          label="Your name"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}
